import { businessEntityApi } from '../../api/businessEntityApi'

import { addNotification, NotificationTypes, notifyException } from './notificationActions'
import { generateUserPermissions, generateUserPermissionsSummary } from './permissionActions.js'

import { currentBusinessEntitySignal, rootBusinessEntitySignal, businessEntitiesLoadedSignal, businessEntitiesErrorSignal, businessEntityDictionarySignal, ancestorBEIDsSetSignal } from "../signals/businessEntitySignals"

import { currentUserSignal } from '../signals/userSignals.js'

import { BusinessEntityType, OrganizationType } from '../../utils/constants'
import { buildCacheKey, checkCache, setCache, invalidateCacheByPrefix } from '../../utils/actionCache'

const BE_CACHE_TTL = 5 * 60 * 1000
const BE_CACHE_PREFIX = 'fetchBusinessEntity'

export async function fetchBusinessEntity(rootBusinessEntityId, config, accessToken, options = { currentBusinessEntityId: null, includeChildren: true, includeParent: true, setType: true, excludeMerchants: true, recomputePermissions: true }, forceRefresh = false) {
	const f = "fetchBusinessEntity"
	let print = false
	businessEntitiesLoadedSignal.value = false
	businessEntityDictionarySignal.value = {}
	rootBusinessEntitySignal.value = null
	currentBusinessEntitySignal.value = null
	ancestorBEIDsSetSignal.value = null
	try {
		const cacheKey = buildCacheKey(BE_CACHE_PREFIX, rootBusinessEntityId)
		let allEntities
		if (!forceRefresh) {
			const cached = checkCache(cacheKey, BE_CACHE_TTL)
			if (cached) {
				allEntities = cached.map(e => ({ ...e }))
			}
		}
		if (!allEntities) {
			allEntities = await businessEntityApi.fetchBusinessEntity(rootBusinessEntityId, config, accessToken)
			setCache(cacheKey, allEntities.map(e => ({ ...e })))
		}
		if (options && !("excludeMerchants" in options)) {
			options.excludeMerchants = true
		}
		if (options && !("setType" in options)) {
			options.setType = true
		}
		if (allEntities.length == 0) {
			if (print) {
				console.log("No entities")
			}
			return null
		}
		let filteredEntities = []
		let children = {}
		// let inactive = 0
		// let active = 0
		let unknown = 0
		for (let i = 0; i < allEntities.length; i++) {
			const be = allEntities[i]
			// if ('isActive' in be) {
			// 	if (be.isActive) {
			// 		active += 1
			// 	}
			// 	else {
			// 		inactive += 1
			// 	}
			// } else {
			// 	unknown += 1
			// }
			// if (options.setType || options.excludeMerchants) {
			// 	if (be.businessEntityTypeId === BusinessEntityType.Merchant) {
			// 		if (options.excludeMerchants) {
			// 			continue
			// 		}
			// 		be.isMerchant = true
			// 		be.isOrganization = false
			// 	}
			// 	else {
			// 		be.isMerchant = false
			// 		if (be.businessEntityTypeId === BusinessEntityType.Organization) {
			// 			be.isOrganization = true
			// 			if (be.businessEntitySubTypeId === OrganizationType.Standard) {
			// 				be.isStandardOrganization = true
			// 				be.isRootOrganization = false
			// 				be.isResellerOrganization = false
			// 			}
			// 			else if (be.businessEntitySubTypeId === OrganizationType.Root) {
			// 				be.isRootOrganization = true
			// 				be.isStandardOrganization = false
			// 				be.isResellerOrganization = false
			// 			}
			// 			else if (be.businessEntitySubTypeId === OrganizationType.Reseller) {
			// 				be.isResellerOrganization = true
			// 				be.isRootOrganization = false
			// 				be.isStandardOrganization = false
			// 			}
			// 		}
			// 		else {
			// 			be.isOrganization = false
			// 		}
			// 	}
			// }

			be.isMerchant = be.businessEntityTypeId == BusinessEntityType.Merchant
			be.isOrganization = be.businessEntityTypeId == BusinessEntityType.OrganizationUnit
			be.isStandardOrganization = be.isOrganization && be.businessEntitySubTypeId == OrganizationType.Standard
			be.isRootOrganization = be.isOrganization && be.businessEntitySubTypeId == OrganizationType.Root
			be.isResellerOrganization = be.isOrganization && be.businessEntitySubTypeId == OrganizationType.Reseller
			be.BusinessEntityFriendlyTypeName = be.isMerchant ? 'Merchant' : be.isResellerOrganization ? 'Reseller' : 'Organization'

			businessEntityDictionarySignal.value[be.id] = be
			be.parentBusinessEntityLoaded = false
			if (be.id == rootBusinessEntityId) { //identify the parent organization
				if (print) {
					console.log("Identified node ".concat(be.id).concat(" as the root BE"))
				}
				rootBusinessEntitySignal.value = be
			}
			if (options.currentBusinessEntityId) {
				if (be.id == options.currentBusinessEntityId) {
					if (print) {
						console.log("Identified node ".concat(be.id).concat(" as the current BE"))
					}
					currentBusinessEntitySignal.value = be
				}
				//keep track of which BEs have which children
				const parentkey = "".concat(be.parentId)
				if (!(parentkey in children)) {
					children[parentkey] = []
				}
				children[parentkey].push(be)
			}
			filteredEntities.push(be)
		}
		// console.log(`Found ${active} active, ${inactive} inactive and ${unknown} unknown business entities`)
		// second pass to assign children to parent business entities and vice versa
		for (let i = 0; i < filteredEntities.length; i++) {
			const be = filteredEntities[i]
			const key = "".concat(be.id)
			if (key in children) {
				for (let j = 0; j < children[key].length; j++) {
					children[key][j].parentBusinessEntity = be
					children[key][j].parentBusinessEntityLoaded = true
				}
				children[key].sort(
					(a, b) => {
						if (a.name.toLowerCase() < b.name.toLowerCase()) {
							return -1
						}
						return 1
					}
				)
				//console.log("Sorted children to the following:")
				//console.log(children[key])
				be.companies = children[key]
			} else {
				be.companies = []
			}
		}
		// get a set of ancestor BE IDs, used by permissions
		if (options.currentBusinessEntityId) {
			let ancestors = new Set()
			let be = currentBusinessEntitySignal.value
			while ('parentBusinessEntity' in be) {
				be = be.parentBusinessEntity
				ancestors.add(be.id)
			}
			ancestorBEIDsSetSignal.value = ancestors
			if (print) {
				let string = []
				for (const beid of ancestorBEIDsSetSignal.value) {
					string.push(beid)
				}
				console.log("Ancestors BE IDs: ".concat(string.join(", ")))
			}
		}
		businessEntitiesLoadedSignal.value = true
		return currentBusinessEntitySignal.value ? currentBusinessEntitySignal.value : rootBusinessEntitySignal.value
	}
	catch (err) {
		businessEntitiesErrorSignal.value = err.message
		notifyException(err, "Error fetching business entity: ".concat(err))
	}
	finally {
		if (currentBusinessEntitySignal.value && options.recomputePermissions) {
			if (print) {
				console.log("Recomputing user permissions after fetching business entity")
			}
			generateUserPermissions()
			generateUserPermissionsSummary()
		}
	}
}

export const validateNewBEAdminEmail = async (config, beId, emailAddress, accessToken) => {
	return await businessEntityApi.validateNewBEAdminEmail(config, beId, emailAddress, accessToken)
}

export const insertOrganization = async (organization, config, accessToken) => {
	invalidateCacheByPrefix(BE_CACHE_PREFIX)
	businessEntitiesLoadedSignal.value = false
	return await businessEntityApi.insertOrganization(organization, config, accessToken).then(
		async (organization) => {
			const options = { currentBusinessEntityId: currentBusinessEntitySignal.value?.id, includeChildren: true, includeParent: true, setType: true, excludeMerchants: true, recomputePermissions: true }
			return await fetchBusinessEntity(rootBusinessEntitySignal.value.id, config, accessToken, options).then(
				() => {
					addNotification(`Successfully submitted new organization `.concat(organization.name), NotificationTypes.SUCCESS, true)
					return organization
				}
			)
		}
	).catch(
		(error) => {
			console.error(`Caught the following exception attempting to insert an organization: ${error.toString()}`)
			businessEntitiesLoadedSignal.value = true
			throw error
		}
	)
}

export const updateOrganization = async (organization, config, accessToken) => {
	invalidateCacheByPrefix(BE_CACHE_PREFIX)
	return await businessEntityApi.updateOrganization(organization, config, accessToken).then(
		async (organization) => {
			const options = { currentBusinessEntityId: currentBusinessEntitySignal.value?.id, includeChildren: true, includeParent: true, setType: true, excludeMerchants: true, recomputePermissions: true }
			return await fetchBusinessEntity(currentUserSignal.value?.businessEntityId, config, accessToken, options)
		}
	).catch(
		(error) => {
			console.error(`Caught the following exception attempting to update an organization: ${error.toString()}`)
			businessEntitiesErrorSignal.value = error
			throw error
		}
	)
}

/**
 * @param {any} businessEntityId
 * @param {any} config
 * @param {any} accessToken
 * @returns
 */
export const deleteOrganization = async (businessEntityId, config, accessToken) => {
	/*
	CREATE Procedure [dbo].[canDeleteOrganization] 
	@BusinessEntityId Int 
	As 

	Declare @CanDelete Bit = 1

	If Exists (Select * From [dbo].[BusinessEntity] Where [Id] = @BusinessEntityId)
	  Begin
		If Exists (Select * From [dbo].[Organization] Where [BusinessEntityId] = @BusinessEntityId)
		  Begin
			If Exists (Select * From [dbo].[User] Where [BusinessEntityId] = @BusinessEntityId) Set @CanDelete = 0
			If Exists (Select * From [dbo].[BusinessEntity] Where [ParentId] = @BusinessEntityId) Set @CanDelete = 0
			If Exists (Select * From [dbo].[GroupBusinessEntity] Where [BusinessEntityId] = @BusinessEntityId) Set @CanDelete = 0
			If Exists (Select * From [dbo].[UserRoleBusinessEntity] Where [BusinessEntityId] = @BusinessEntityId) Set @CanDelete = 0
		  End
	  End

	Select Cast(@CanDelete As Bit)
	*/
	invalidateCacheByPrefix(BE_CACHE_PREFIX)
	businessEntitiesLoadedSignal.value = false
	return await businessEntityApi.deleteOrganization(businessEntityId, config, accessToken).then(
		async (response) => {
			const options = { currentBusinessEntityId: currentBusinessEntitySignal.value?.id, includeChildren: true, includeParent: true, setType: true, excludeMerchants: true, recomputePermissions: true }
			return await fetchBusinessEntity(rootBusinessEntitySignal.value, config, accessToken, options).then(
				() => {
					return response
				}
			)
		}
	).catch(
		(error) => {
			console.error(`Caught the following exception attempting to delete an organization: ${error.toString()}`)
			businessEntitiesErrorSignal.value = error
			throw error
		}
	)
}

export const deactivateOrganization = async (businessEntityId, config, accessToken) => {
	invalidateCacheByPrefix(BE_CACHE_PREFIX)
	businessEntitiesLoadedSignal.value = false
	return await businessEntityApi.deactivateOrganization(businessEntityId, config, accessToken).then(
		(organization) => {
			currentBusinessEntitySignal.value = organization
			businessEntitiesLoadedSignal.value = true
			return organization
		}
	).catch(
		(error) => {
			console.error(`Caught the following exception attempting to deactivate an organization: ${error.toString()}`)
			businessEntitiesErrorSignal.value = error
			throw error
		}
	)
}

export const insertMerchant = async (merchant, config, accessToken) => {
	invalidateCacheByPrefix(BE_CACHE_PREFIX)
	businessEntitiesLoadedSignal.value = false
	return await businessEntityApi.insertMerchant(merchant, config, accessToken).then(
		async (merchant) => {
			const options = { currentBusinessEntityId: currentBusinessEntitySignal.value?.id, includeChildren: true, includeParent: true, setType: true, excludeMerchants: false, recomputePermissions: true }
			return await fetchBusinessEntity(rootBusinessEntitySignal.value, config, accessToken, options).then(
				() => {
					addNotification(`Successfully submitted new merchant `.concat(merchant.name), NotificationTypes.SUCCESS, true)
					return merchant
				}
			)
		}
	).catch(
		(error) => {
			console.error(`Caught the following exception attempting to submit a merchant: ${error.toString()}`)
			businessEntitiesLoadedSignal.value = true
			throw error
		}
	)
}

export const updateMerchant = async (merchant, config, accessToken) => {
	invalidateCacheByPrefix(BE_CACHE_PREFIX)
	return await businessEntityApi.updateMerchant(merchant, config, accessToken).then(
		async () => {
			const options = { currentBusinessEntityId: currentBusinessEntitySignal.value?.id, includeChildren: true, includeParent: true, setType: true, excludeMerchants: false, recomputePermissions: true }
			return await fetchBusinessEntity(currentUserSignal.value?.businessEntityId, config, accessToken, options)
		}
	).catch(
		(error) => {
			console.error(`Caught the following exception attempting to update a merchant: ${error.toString()}`)
			businessEntitiesErrorSignal.value = error
			throw error
		}
	)
}

export const deleteMerchant = async (businessEntityId, config, accessToken) => {
	invalidateCacheByPrefix(BE_CACHE_PREFIX)
	businessEntitiesLoadedSignal.value = false
	return businessEntityApi.deleteMerchant(businessEntityId, config, accessToken).then(
		async (response) => {
			const options = { currentBusinessEntityId: currentBusinessEntitySignal.value?.id, includeChildren: true, includeParent: true, setType: true, excludeMerchants: false, recomputePermissions: true }
			return await fetchBusinessEntity(rootBusinessEntitySignal.value, config, accessToken, options).then(
				() => {
					return response
				}
			)
		}
	).catch(
		(error) => {
			console.error(`Caught the following exception attempting to delete a merchant: ${error.toString()}`)
			businessEntitiesErrorSignal.value = error
			throw error
		}
	)
}